import { LRUCache } from './lru-cache';

type CacheEntry = {
  error?: Error;
  response?: Promise<Response>;
  timestamp: number;
};

const cache = new LRUCache<string, CacheEntry>(256);
const CACHE_DURATION = 60 * 1000;

export async function cachedFetch(
  url: string | URL | globalThis.Request,
  options?: RequestInit
): Promise<Response> {
  if (options?.method && options.method !== "GET") {
    return fetch(url, options);
  }

  // Extract the proper URL string and headers for caching
  const cacheKey = [
    url instanceof Request ? url.url : url.toString(),
    JSON.stringify(Object.fromEntries(new Headers(options?.headers || {}))),
  ].join("|");
  const cached = cache.get(cacheKey);
  const startedAt = Date.now();

  // Return from cache if valid
  if (cached && startedAt - cached.timestamp < CACHE_DURATION) {
    if (cached.error) {
      throw cached.error;
    } else if (cached.response) {
      return cached.response.then((res) => res.clone());
    }
  }

  // Create and cache the new promise
  const response = fetch(url, options).catch((error) => {
    cache.set(cacheKey, {
      timestamp: startedAt,
      error: error as Error,
      response,
    });
    throw error;
  });

  cache.set(cacheKey, {
    timestamp: startedAt,
    error: undefined,
    response,
  });

  return response.then((res) => res.clone());
}
